import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Play, Award, ListChecks, TrendingUp, ArrowUpRight, Loader2, ShoppingCart, Lock, Upload, X, Trash2, FileText, FileSpreadsheet, FileArchive, Presentation, Download, Plus, ArrowUp, ArrowDown } from "lucide-react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FORMATIONS, LEARNER_PROFILES } from "../data/content";
import PageHeader from "../components/PageHeader";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOD_ICON = { pdf: FileText, doc: FileText, docx: FileText, txt: FileText, ppt: Presentation, pptx: Presentation, xls: FileSpreadsheet, xlsx: FileSpreadsheet, csv: FileSpreadsheet, zip: FileArchive };
const MOD_ACCEPT = ".pdf,.ppt,.pptx,.doc,.docx,.xls,.xlsx,.zip,.txt,.csv";

const LEVEL_STYLE = {
  "Débutant": "bg-emerald-500/15 text-emerald-700 border-emerald-600/30",
  "Intermédiaire": "bg-gold/15 text-gold border-gold/40",
  "Expert": "bg-ink/10 text-ink border-ink/25",
};

const FEATURES = [
  { icon: Play, t: "Vidéos & modules", d: "Des parcours vidéo structurés, accessibles à votre rythme." },
  { icon: ListChecks, t: "Quiz interactifs", d: "Validez vos acquis à chaque étape du parcours." },
  { icon: Award, t: "Certificats", d: "Une certification reconnue à la fin de chaque formation." },
  { icon: TrendingUp, t: "Suivi de progression", d: "Un tableau de bord clair de la montée en compétences." },
];

export default function Formation() {
  const [loadingKey, setLoadingKey] = useState(null);
  const { add: addToCart } = useCart();
  const { user } = useAuth();
  const [modules, setModules] = useState([]);
  const [owned, setOwned] = useState([]);
  const [showCreator, setShowCreator] = useState(false);
  const [creatorTab, setCreatorTab] = useState("module");
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem("nlc_admin_key") || "");
  const [verified, setVerified] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [modForm, setModForm] = useState({ formation_lookup: FORMATIONS[0]?.lookup || "", title: "", description: "", file: null });
  const [quizLookup, setQuizLookup] = useState(FORMATIONS[0]?.lookup || "");
  const [quiz, setQuiz] = useState({ passing_score: 80, max_attempts: 3, certification_enabled: true, questions: [] });
  const [quizLoaded, setQuizLoaded] = useState(false);
  const isAdmin = Boolean(verified && adminKey);

  const loadQuiz = useCallback(async (lookup) => {
    try {
      const { data } = await axios.get(`${API}/admin/quiz/${lookup}`, { headers: { "x-admin-key": adminKey } });
      setQuiz({ passing_score: data.passing_score ?? 80, max_attempts: data.max_attempts ?? 3, certification_enabled: data.certification_enabled ?? true, questions: data.questions || [] });
      setQuizLoaded(true);
    } catch (e) { toast.error("Chargement du QCM impossible."); }
  }, [adminKey]);

  const setQ = (i, patch) => setQuiz((q) => ({ ...q, questions: q.questions.map((x, xi) => (xi === i ? { ...x, ...patch } : x)) }));
  const setOpt = (qi, oi, v) => setQuiz((q) => ({ ...q, questions: q.questions.map((x, xi) => xi === qi ? { ...x, options: x.options.map((o, oi2) => (oi2 === oi ? v : o)) } : x) }));
  const addQuestion = () => setQuiz((q) => ({ ...q, questions: [...q.questions, { id: "", question: "", options: ["", "", "", ""], correct_index: 0, explanation: "" }] }));
  const removeQuestion = (i) => setQuiz((q) => ({ ...q, questions: q.questions.filter((_, xi) => xi !== i) }));
  const moveQuestion = (i, dir) => setQuiz((q) => {
    const arr = [...q.questions]; const j = i + dir;
    if (j < 0 || j >= arr.length) return q;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return { ...q, questions: arr };
  });

  const saveQuiz = async () => {
    setUploading(true);
    try {
      await axios.put(`${API}/admin/quiz/${quizLookup}`, quiz, { headers: { "x-admin-key": adminKey } });
      toast.success("QCM enregistré !");
    } catch (e) { toast.error("Enregistrement impossible.", { description: e?.response?.data?.detail }); }
    finally { setUploading(false); }
  };

  const loadModules = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/modules`); setModules(data || []); } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { loadModules(); }, [loadModules]);
  useEffect(() => {
    if (!user) { setOwned([]); return; }
    axios.get(`${API}/me/entitlements`).then(({ data }) => setOwned(data.lookup_keys || [])).catch(() => {});
  }, [user]);

  const verifyKey = async () => {
    try {
      await axios.post(`${API}/admin/verify`, {}, { headers: { "x-admin-key": adminKey } });
      localStorage.setItem("nlc_admin_key", adminKey);
      setVerified(true);
      toast.success("Espace créateur déverrouillé.");
    } catch (e) { toast.error("Clé invalide."); }
  };

  const downloadModule = async (m) => {
    try {
      const res = await axios.get(`${API}/modules/${m.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = `${m.title}.${m.file_ext || "bin"}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Téléchargement impossible.", { description: "Cette formation n'est peut-être pas dans vos achats." }); }
  };

  const uploadModule = async (e) => {
    e.preventDefault();
    if (!modForm.title || !modForm.file) return toast.error("Titre et fichier requis.");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("formation_lookup", modForm.formation_lookup);
      fd.append("title", modForm.title);
      fd.append("description", modForm.description);
      fd.append("file", modForm.file);
      await axios.post(`${API}/modules`, fd, { headers: { "x-admin-key": adminKey } });
      toast.success("Module publié !");
      setModForm({ formation_lookup: FORMATIONS[0]?.lookup || "", title: "", description: "", file: null });
      loadModules();
    } catch (e) { toast.error("Publication impossible.", { description: e?.response?.data?.detail || "Réessayez." }); }
    finally { setUploading(false); }
  };

  const removeModule = async (id) => {
    try { await axios.delete(`${API}/modules/${id}`, { headers: { "x-admin-key": adminKey } }); toast.success("Module retiré."); loadModules(); }
    catch (e) { toast.error("Suppression impossible."); }
  };

  const modulesFor = (lookup) => modules.filter((m) => m.formation_lookup === lookup);

  const enroll = async (f) => {
    setLoadingKey(f.lookup);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, {
        lookup_key: f.lookup,
        origin_url: window.location.origin,
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error("no url");
      }
    } catch (e) {
      toast.error("Impossible d'ouvrir le paiement.", { description: "Veuillez réessayer dans un instant." });
      setLoadingKey(null);
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="[ Centre de Formation ]"
        titleLines={["Montez vos équipes", "en compétences."]}
        intro="Une plateforme e-learning dédiée aux métiers de l'industrie, du Lean Manufacturing au Power BI industriel. Trois niveaux, des certificats, un suivi personnalisé."
      />

      <section className="bg-paper py-20 md:py-28" data-testid="formation-profiles">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <Reveal><span className="label-mono text-gold">[ Pour qui ? ]</span></Reveal>
          <div className="flex flex-wrap gap-3 mt-8">
            {LEARNER_PROFILES.map((p, i) => (
              <Reveal key={p} delay={i * 0.04}>
                <span className="inline-block rounded-full border hairline bg-white px-5 py-3 font-display text-lg md:text-xl font-medium">{p}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-paper pb-24 md:pb-32" data-testid="formation-catalog">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <div className="flex items-end justify-between gap-6 mb-12">
            <Reveal>
              <span className="label-mono text-gold">[ Catalogue ]</span>
              <h2 className="font-display text-4xl md:text-6xl font-semibold tracking-tight mt-4 leading-[0.95]">Nos formations</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <button onClick={() => setShowCreator(true)} data-testid="open-module-creator"
                className="group inline-flex items-center gap-2 rounded-full border hairline px-5 py-2.5 label-mono text-ink hover:bg-ink hover:text-paper transition-colors">
                <Lock size={14} /> Espace créateur
              </button>
            </Reveal>
          </div>
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FORMATIONS.map((f) => (
              <StaggerItem key={f.title}>
                <article className="group h-full flex flex-col rounded-sm overflow-hidden border hairline bg-white hover:shadow-[0_20px_50px_-20px_rgba(10,26,51,0.35)] transition-shadow duration-500" data-testid={`formation-${f.title}`}>
                  <div className="relative h-44 overflow-hidden">
                    <img src={f.image} alt={f.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[1100ms] ease-out" />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink-900/60 to-transparent" />
                    <span className="absolute top-4 left-4 label-mono text-paper bg-ink-900/70 backdrop-blur px-3 py-1.5 rounded-full">{f.tag}</span>
                    <div className="absolute bottom-4 right-4 w-11 h-11 rounded-full bg-gold flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                      <Play size={16} className="text-ink ml-0.5" fill="currentColor" />
                    </div>
                  </div>
                  <div className="flex flex-col flex-1 p-6">
                    <h3 className="font-display text-2xl font-semibold leading-tight">{f.title}</h3>
                    <div className="flex items-center gap-3 mt-4">
                      <span className={`label-mono px-3 py-1 rounded-full border ${LEVEL_STYLE[f.level]}`}>{f.level}</span>
                      <span className="label-mono text-ink/45">{f.duration}</span>
                    </div>
                    {modulesFor(f.lookup).length > 0 && (
                      <div className="mt-5 pt-4 border-t hairline" data-testid={`modules-${f.lookup}`}>
                        <span className="label-mono text-ink/40">Modules inclus ({modulesFor(f.lookup).length})</span>
                        <ul className="mt-2.5 space-y-1.5">
                          {modulesFor(f.lookup).map((m) => {
                            const Icon = MOD_ICON[m.file_ext] || FileText;
                            const canGet = owned.includes(f.lookup);
                            return (
                              <li key={m.id} className="flex items-center gap-2.5 text-sm">
                                <Icon size={14} className="text-gold shrink-0" />
                                <span className="text-ink/75 flex-1 truncate">{m.title}</span>
                                {canGet ? (
                                  <button onClick={() => downloadModule(m)} data-testid={`download-module-${m.id}`} aria-label="Télécharger" className="text-ink/50 hover:text-gold transition-colors shrink-0"><Download size={15} /></button>
                                ) : (
                                  <Lock size={13} className="text-ink/30 shrink-0" />
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                    <div className="flex items-center justify-between mt-auto pt-6 border-t hairline">
                      <span className="font-display text-2xl font-bold text-ink">{f.price} €<span className="label-mono text-ink/40 font-normal"> HT</span></span>
                      {owned.includes(f.lookup) ? (
                        <Link to={`/formation/${f.lookup}/apprendre`} data-testid={`access-${f.lookup}`}
                          className="group/btn inline-flex items-center gap-2 rounded-full bg-gold text-ink px-5 py-2.5 label-mono hover:bg-ink hover:text-paper transition-colors duration-300">
                          Accéder <Play size={13} />
                        </Link>
                      ) : (
                      <div className="flex items-center gap-2">
                        <button onClick={() => addToCart({ key: f.lookup, kind: "formation", name: f.title, price: f.price })} data-testid={`add-cart-${f.lookup}`} aria-label="Ajouter au panier"
                          className="w-10 h-10 inline-flex items-center justify-center rounded-full border hairline text-ink hover:border-gold hover:text-gold transition-colors">
                          <ShoppingCart size={15} />
                        </button>
                        <button
                          onClick={() => enroll(f)}
                          disabled={loadingKey === f.lookup}
                          data-testid={`enroll-${f.lookup}`}
                          className="group/btn inline-flex items-center gap-2 rounded-full bg-ink text-paper px-5 py-2.5 label-mono hover:bg-gold hover:text-ink transition-colors duration-300 disabled:opacity-60"
                        >
                          {loadingKey === f.lookup ? <><Loader2 size={14} className="animate-spin" /> ...</> : <>S'inscrire <ArrowUpRight size={13} className="group-hover/btn:rotate-45 transition-transform" /></>}
                        </button>
                      </div>
                      )}
                    </div>
                  </div>
                </article>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="bg-ink-900 text-paper grain relative py-24 md:py-32" data-testid="formation-features">
        <div className="relative z-10 mx-auto max-w-[1400px] px-5 md:px-10">
          <Reveal>
            <span className="label-mono text-gold">[ La plateforme ]</span>
            <h2 className="font-display text-4xl md:text-6xl font-semibold tracking-tight mt-4 max-w-2xl leading-[0.95]">Tout pour apprendre, valider et certifier.</h2>
          </Reveal>
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 mt-14 border hairline-light rounded-sm overflow-hidden">
            {FEATURES.map((f) => (
              <StaggerItem key={f.t} className="bg-ink-900 p-8 hover:bg-ink-800 transition-colors duration-500">
                <f.icon size={28} className="text-gold" />
                <h3 className="font-display text-xl font-semibold mt-6">{f.t}</h3>
                <p className="text-paper/60 text-sm mt-3 leading-relaxed">{f.d}</p>
              </StaggerItem>
            ))}
          </Stagger>
          <Reveal delay={0.2}>
            <Link to="/contact" data-testid="formation-cta" className="group inline-flex items-center gap-3 mt-14 rounded-full bg-gold text-ink px-8 py-4 label-mono hover:bg-paper transition-colors duration-300">
              Inscrire mes équipes <ArrowUpRight size={16} className="group-hover:rotate-45 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>

      <AnimatePresence>
        {showCreator && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] bg-ink-950/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={() => setShowCreator(false)} data-testid="module-creator">
            <motion.div initial={{ opacity: 0, y: 30, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20 }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="relative bg-paper text-ink rounded-sm max-w-lg w-full p-8 my-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <button onClick={() => setShowCreator(false)} className="absolute top-4 right-4 w-9 h-9 rounded-full border hairline flex items-center justify-center text-ink/60 hover:bg-ink hover:text-paper transition-colors"><X size={16} /></button>
              <span className="label-mono text-gold">Espace créateur</span>
              <h2 className="font-display text-3xl font-semibold mt-2">{creatorTab === "module" ? "Ajouter un module de formation" : "Configurer le QCM final"}</h2>
              {isAdmin && (
                <div className="flex gap-2 mt-5">
                  <button onClick={() => setCreatorTab("module")} data-testid="creator-tab-module" className={`label-mono px-4 py-2 rounded-full border transition-colors ${creatorTab === "module" ? "bg-ink text-paper border-ink" : "text-ink/70 hairline hover:border-ink"}`}>Module</button>
                  <button onClick={() => { setCreatorTab("qcm"); if (!quizLoaded) loadQuiz(quizLookup); }} data-testid="creator-tab-qcm" className={`label-mono px-4 py-2 rounded-full border transition-colors ${creatorTab === "qcm" ? "bg-ink text-paper border-ink" : "text-ink/70 hairline hover:border-ink"}`}>QCM</button>
                </div>
              )}
              {!isAdmin ? (
                <div className="mt-8">
                  <label className="label-mono text-ink/60 block mb-2">Clé créateur</label>
                  <input type="password" value={adminKey} onChange={(e) => setAdminKey(e.target.value)} placeholder="Votre clé" data-testid="module-admin-key" className="w-full bg-white border hairline rounded-sm px-4 py-3 outline-none focus:border-gold" />
                  <button onClick={verifyKey} data-testid="module-admin-verify" className="w-full mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-ink text-paper px-6 py-3.5 label-mono hover:bg-gold hover:text-ink transition-colors">Déverrouiller</button>
                  <p className="text-ink/45 text-xs mt-4">Réservé à l'équipe NovaLean.</p>
                </div>
              ) : creatorTab === "module" ? (
                <form onSubmit={uploadModule} className="mt-6 space-y-4" data-testid="module-upload-form">
                  <div>
                    <label className="label-mono text-ink/60 block mb-2">Formation *</label>
                    <select value={modForm.formation_lookup} onChange={(e) => setModForm({ ...modForm, formation_lookup: e.target.value })} data-testid="module-formation" className="w-full bg-white border hairline rounded-sm px-4 py-3 outline-none focus:border-gold">
                      {FORMATIONS.map((f) => <option key={f.lookup} value={f.lookup}>{f.title}</option>)}
                    </select>
                  </div>
                  <div><label className="label-mono text-ink/60 block mb-2">Titre du module *</label><input value={modForm.title} onChange={(e) => setModForm({ ...modForm, title: e.target.value })} placeholder="ex. Module 1 — Introduction au Lean" data-testid="module-title" className="w-full bg-white border hairline rounded-sm px-4 py-3 outline-none focus:border-gold" /></div>
                  <div><label className="label-mono text-ink/60 block mb-2">Description (optionnel)</label><textarea value={modForm.description} onChange={(e) => setModForm({ ...modForm, description: e.target.value })} rows={2} data-testid="module-description" className="w-full bg-white border hairline rounded-sm px-4 py-3 outline-none focus:border-gold resize-none" /></div>
                  <div>
                    <label className="label-mono text-ink/60 block mb-2">Fichier du module * <span className="text-ink/35">(PPT, PDF, DOC, XLS, ZIP…)</span></label>
                    <input type="file" accept={MOD_ACCEPT} onChange={(e) => setModForm({ ...modForm, file: e.target.files[0] })} data-testid="module-file" className="w-full text-sm file:mr-3 file:rounded-full file:border-0 file:bg-ink file:text-paper file:px-4 file:py-2 file:label-mono" />
                  </div>
                  <button type="submit" disabled={uploading} data-testid="publish-module-btn" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold text-ink px-6 py-4 label-mono hover:bg-ink hover:text-paper transition-colors disabled:opacity-60">{uploading ? <><Loader2 size={16} className="animate-spin" /> Publication...</> : <><Upload size={15} /> Publier le module</>}</button>
                  {modules.length > 0 && (
                    <div className="pt-4 border-t hairline">
                      <span className="label-mono text-ink/40">Modules publiés ({modules.length})</span>
                      <ul className="mt-3 space-y-2 max-h-48 overflow-y-auto">
                        {modules.map((m) => (
                          <li key={m.id} className="flex items-center gap-2.5 text-sm bg-white border hairline rounded-sm px-3 py-2">
                            <span className="label-mono text-gold shrink-0 uppercase">{m.file_ext}</span>
                            <span className="text-ink/75 flex-1 truncate">{m.title}</span>
                            <button type="button" onClick={() => removeModule(m.id)} data-testid={`delete-module-${m.id}`} aria-label="Supprimer" className="text-ink/40 hover:text-red-500 transition-colors shrink-0"><Trash2 size={14} /></button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </form>
              ) : (
                <div className="mt-6 space-y-4" data-testid="qcm-editor">
                  <div>
                    <label className="label-mono text-ink/60 block mb-2">Formation</label>
                    <select value={quizLookup} onChange={(e) => { setQuizLookup(e.target.value); loadQuiz(e.target.value); }} data-testid="qcm-formation" className="w-full bg-white border hairline rounded-sm px-4 py-3 outline-none focus:border-gold">
                      {FORMATIONS.map((f) => <option key={f.lookup} value={f.lookup}>{f.title}</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div><label className="label-mono text-ink/60 block mb-2">Seuil (%)</label><input type="number" min="0" max="100" value={quiz.passing_score} onChange={(e) => setQuiz({ ...quiz, passing_score: Number(e.target.value) })} data-testid="qcm-passing" className="w-full bg-white border hairline rounded-sm px-3 py-2.5 outline-none focus:border-gold" /></div>
                    <div><label className="label-mono text-ink/60 block mb-2">Tentatives (0=∞)</label><input type="number" min="0" value={quiz.max_attempts} onChange={(e) => setQuiz({ ...quiz, max_attempts: Number(e.target.value) })} data-testid="qcm-attempts" className="w-full bg-white border hairline rounded-sm px-3 py-2.5 outline-none focus:border-gold" /></div>
                    <div className="flex items-end pb-1">
                      <label className="flex items-center gap-2 cursor-pointer text-sm text-ink/75">
                        <input type="checkbox" checked={quiz.certification_enabled} onChange={(e) => setQuiz({ ...quiz, certification_enabled: e.target.checked })} data-testid="qcm-cert-enabled" className="w-4 h-4 accent-[#C8992F]" /> Certificat actif
                      </label>
                    </div>
                  </div>
                  <div className="space-y-4 max-h-[42vh] overflow-y-auto pr-1">
                    {quiz.questions.map((q, qi) => (
                      <div key={qi} className="border hairline rounded-sm bg-white p-4" data-testid={`qcm-edit-q${qi}`}>
                        <div className="flex items-center justify-between mb-3">
                          <span className="label-mono text-gold">Question {qi + 1}</span>
                          <div className="flex gap-1">
                            <button type="button" onClick={() => moveQuestion(qi, -1)} aria-label="Monter" className="w-7 h-7 rounded-full border hairline flex items-center justify-center text-ink/50 hover:text-ink"><ArrowUp size={12} /></button>
                            <button type="button" onClick={() => moveQuestion(qi, 1)} aria-label="Descendre" className="w-7 h-7 rounded-full border hairline flex items-center justify-center text-ink/50 hover:text-ink"><ArrowDown size={12} /></button>
                            <button type="button" onClick={() => removeQuestion(qi)} data-testid={`qcm-del-q${qi}`} aria-label="Supprimer" className="w-7 h-7 rounded-full border hairline flex items-center justify-center text-ink/50 hover:text-red-500"><Trash2 size={12} /></button>
                          </div>
                        </div>
                        <input value={q.question} onChange={(e) => setQ(qi, { question: e.target.value })} placeholder="Intitulé de la question" data-testid={`qcm-question-${qi}`} className="w-full bg-paper border hairline rounded-sm px-3 py-2.5 text-sm outline-none focus:border-gold mb-2" />
                        <div className="grid grid-cols-2 gap-2">
                          {q.options.map((opt, oi) => (
                            <label key={oi} className={`flex items-center gap-2 rounded-sm border px-2.5 py-2 ${q.correct_index === oi ? "border-green-400 bg-green-50/60" : "hairline"}`}>
                              <input type="radio" name={`correct-${qi}`} checked={q.correct_index === oi} onChange={() => setQ(qi, { correct_index: oi })} data-testid={`qcm-correct-${qi}-${oi}`} className="accent-green-600 shrink-0" />
                              <input value={opt} onChange={(e) => setOpt(qi, oi, e.target.value)} placeholder={`Réponse ${String.fromCharCode(65 + oi)}`} data-testid={`qcm-option-${qi}-${oi}`} className="w-full bg-transparent text-sm outline-none" />
                            </label>
                          ))}
                        </div>
                        <input value={q.explanation} onChange={(e) => setQ(qi, { explanation: e.target.value })} placeholder="Explication (affichée après correction, optionnel)" data-testid={`qcm-explanation-${qi}`} className="w-full bg-paper border hairline rounded-sm px-3 py-2 text-sm outline-none focus:border-gold mt-2" />
                      </div>
                    ))}
                  </div>
                  <button type="button" onClick={addQuestion} data-testid="qcm-add-question" className="w-full inline-flex items-center justify-center gap-2 rounded-full border hairline px-5 py-3 label-mono text-ink hover:bg-ink hover:text-paper transition-colors"><Plus size={14} /> Ajouter une question</button>
                  <button onClick={saveQuiz} disabled={uploading || quiz.questions.length === 0} data-testid="qcm-save" className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold text-ink px-6 py-4 label-mono hover:bg-ink hover:text-paper transition-colors disabled:opacity-60">
                    {uploading ? <><Loader2 size={16} className="animate-spin" /> Enregistrement...</> : <>Enregistrer le QCM ({quiz.questions.length} questions)</>}
                  </button>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
